import {type ReactNode, useEffect, useRef} from 'react'
import {Link} from 'react-router-dom'
import {
  ArrowRight, DownloadIcon, SourcesIcon, ProduceIcon, ExplainIcon,
  MapViz, TableViz, ChartViz, FileViz, ContourField
} from './landing-visuals'
import './landing.css'

/* ----------------------------------------------------------------------------
   Page d'accueil Living Data Studio.
   Copy française validée (cf. plan marketing), montée en CSS bespoke
   (landing.css) : studio-front consomme le CSS compilé de core-front et ne
   génère pas ses propres utilitaires Tailwind, donc tout le style vit dans le
   fichier dédié et la structure ici reste sémantique.
---------------------------------------------------------------------------- */

const SIGN_UP = '/sign-up'
const PRICING = '/pricing'
const DOCS = '/docs'

// Observe les éléments .ld-reveal / .ld-panel et déclenche leur apparition une
// fois entrés dans le viewport. Un seul observer pour toute la page.
function useReveal() {
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = root.current
    if (!el) {
      return
    }

    const targets = el.querySelectorAll('.ld-reveal, .ld-panel')
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        }
      },
      {threshold: 0.16, rootMargin: '0px 0px -8% 0px'}
    )

    for (const target of targets) {
      observer.observe(target)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  return root
}

/* ---- Petits blocs réutilisables ----------------------------------------- */

function Eyebrow({children}: {readonly children: ReactNode}) {
  return <p className='ld-eyebrow'>{children}</p>
}

function Reveal({children, delay, className}: {readonly children: ReactNode; readonly delay?: 1 | 2 | 3; readonly className?: string}) {
  return (
    <div className={`ld-reveal${className ? ` ${className}` : ''}`} data-delay={delay}>
      {children}
    </div>
  )
}

/* ======================================================================== */
/* Sections                                                                  */
/* ======================================================================== */

function Hero() {
  return (
    <section className='ld-section ld-hero'>
      <div className='ld-wrap ld-hero__grid'>
        <div className='ld-hero__copy'>
          <Eyebrow>Living Data Studio</Eyebrow>
          <h1 className='ld-h1'>
            Votre espace de travail data. Vous restez <span className='ld-mark'>aux commandes</span>.
          </h1>
          <p className='ld-lead ld-hero__sub'>
            L’agent expert nettoie, enrichit et valorise vos données, et vous rend des résultats prêts à l’emploi.
          </p>
          <div className='ld-hero__cta'>
            <Link to={SIGN_UP} className='ld-btn ld-btn--primary'>
              Créer un compte <ArrowRight />
            </Link>
            <Link to={PRICING} className='ld-btn ld-btn--ghost'>
              Voir les offres
            </Link>
          </div>
          <p className='ld-hero__note'>Early Access — gratuit au lancement, de quoi tester la solution en conditions réelles.</p>
        </div>

        <AgentPanel />
      </div>
    </section>
  )
}

// Panneau produit : une demande en français devient un vrai traitement, puis
// un objet exploitable. C'est la preuve visuelle de « agent, pas chatbot ».
function AgentPanel() {
  return (
    <div className='ld-panel' aria-label='Aperçu : de la demande au résultat'>
      <div className='ld-panel__bar'>
        <span className='ld-panel__dot' /><span className='ld-panel__dot' /><span className='ld-panel__dot' />
        <span className='ld-panel__title'>studio · espace de travail</span>
      </div>

      <div className='ld-msg'>
        <span className='ld-msg__avatar ld-msg__avatar--user'>VS</span>
        <p className='ld-msg__text'>Géocode mon export de ventes 2024, rapproche-le des parcelles cadastrales et sors un fichier des parcelles concernées.</p>
      </div>

      <div className='ld-msg'>
        <span className='ld-msg__avatar ld-msg__avatar--agent'>LD</span>
        <p className='ld-msg__text'>Je géocode les adresses, je récupère le cadastre, je fais la jointure, puis je rends la carte et le fichier.</p>
      </div>

      <ul className='ld-steps'>
        <li className='ld-step'><span className='ld-step__tick'>✓</span> géocodage des 1 240 adresses</li>
        <li className='ld-step'><span className='ld-step__tick'>✓</span> jointure au parcellaire (cadastre)</li>
        <li className='ld-step'><span className='ld-step__tick'>✓</span> export GeoPackage · 1 187 parcelles</li>
      </ul>

      <div className='ld-artifact'>
        <div className='ld-artifact__head'>
          <div className='ld-artifact__tabs'>
            <span className='ld-tab ld-tab--active'>Carte</span>
            <span className='ld-tab'>Tableau</span>
            <span className='ld-tab'>Données</span>
          </div>
          <span className='ld-artifact__meta'>3 sources</span>
        </div>
        <MapViz className='ld-artifact__map' />
        <div className='ld-artifact__foot'>
          <span className='ld-artifact__meta'>ventes_parcelles.gpkg</span>
          <span className='ld-dl'><DownloadIcon /> Télécharger</span>
        </div>
      </div>
    </div>
  )
}

const CHORES = [
  'Un export à nettoyer.',
  'Deux bases à rapprocher sur une clé qui ne tombe jamais juste.',
  'Un fichier d’adresses à géocoder.',
  'Trois couches à superposer, chacune dans sa projection.'
]

function Problem() {
  return (
    <section className='ld-section ld-section--pad ld-problem'>
      <div className='ld-wrap ld-problem__grid'>
        <Reveal>
          <Eyebrow>Le point de départ</Eyebrow>
          <h2 className='ld-h2'>Vous avez les données. Pas le temps de les exploiter.</h2>
        </Reveal>

        <Reveal delay={1}>
          <ul className='ld-chore-list'>
            {CHORES.map((chore, i) => (
              <li key={chore} className='ld-chore'>
                <span className='ld-chore__idx'>{String(i + 1).padStart(2, '0')}</span>
                <span>{chore}</span>
              </li>
            ))}
          </ul>
          <p className='ld-body' style={{marginTop: '1.1rem'}}>
            D’habitude, ça veut dire un tableur ouvert depuis ce matin, un script à débugger, un logiciel lourd, ou un message à la personne qui maîtrise l’outil et croule déjà sous les demandes.
          </p>
        </Reveal>
      </div>
    </section>
  )
}

const PROOFS = [
  {
    icon: <ExplainIcon />,
    title: 'Un travail transparent.',
    body: 'Étapes, sources, traitements appliqués : tout reste visible, en direct.',
    sources: ['Workflow', 'Replay']
  },
  {
    icon: <ProduceIcon />,
    title: 'Un environnement de calcul Linux.',
    body: 'Isolé et outillé pour la donnée. Nettoyage, jointures, calculs et analyses y sont exécutés à la demande.',
    sources: ['Python', 'GDAL', 'DuckDB']
  },
  {
    icon: <SourcesIcon />,
    title: 'Des référentiels de confiance.',
    body: 'La donnée vient de la source qui fait foi, jamais d’une approximation.',
    sources: ['Données publiques de référence', 'Vos catalogues', 'Vos fichiers']
  }
]

function Agent() {
  return (
    <section className='ld-section ld-section--pad'>
      <div className='ld-wrap'>
        <Reveal>
          <Eyebrow>Le moteur</Eyebrow>
          <h2 className='ld-h2' style={{maxWidth: '24ch'}}>
            Les traitements s’<span className='ld-produced'>exécutent</span>, vous suivez chaque étape.
          </h2>
          <p className='ld-lead' style={{maxWidth: '46rem'}}>
            Décrivez votre besoin en langage courant. Aucun script à écrire, aucun logiciel à installer.
          </p>
        </Reveal>

        <div className='ld-proof-grid'>
          {PROOFS.map((proof, i) => (
            <Reveal key={proof.title} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <article className='ld-proof'>
                <div className='ld-proof__icon'>{proof.icon}</div>
                <h3 className='ld-h3'>{proof.title}</h3>
                <p className='ld-body' style={{fontSize: '0.95rem'}}>{proof.body}</p>
                {proof.sources.length > 0 && (
                  <div className='ld-proof__sources'>
                    {proof.sources.map(source => (
                      <span key={source} className='ld-chip'>{source}</span>
                    ))}
                  </div>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

const OBJECTS = [
  {kind: 'Carte', use: 'Du pays à la parcelle.', viz: <MapViz />},
  {kind: 'Tableau', use: 'Tri et filtres instantanés.', viz: <TableViz />},
  {kind: 'Graphique', use: 'Barres, courbes, secteurs…', viz: <ChartViz />},
  {kind: 'Fichier', use: 'GeoPackage, CSV, Parquet…', viz: <FileViz />}
]

function Results() {
  return (
    <section className='ld-section ld-section--pad'>
      <div className='ld-wrap'>
        <Reveal>
          <Eyebrow>Les résultats</Eyebrow>
          <h2 className='ld-h2'>Interactifs, partageables par un lien.</h2>
          <p className='ld-lead' style={{maxWidth: '44rem'}}>
            Le fichier produit, lui, se télécharge — et pour un autre format, il suffit de le demander à l’agent.
          </p>
        </Reveal>

        <div className='ld-bento'>
          {OBJECTS.map((object, i) => (
            <Reveal key={object.kind} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <article className='ld-object'>
                <div className='ld-object__viz'>{object.viz}</div>
                <div className='ld-object__body'>
                  <span className='ld-object__kind'>{object.kind}</span>
                  <p className='ld-object__use'>{object.use}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

const WHY = [
  {title: 'Le secteur public, de l’intérieur.', body: 'Living Data outille les administrations et les acteurs publics. Ce métier est encodé dans l’agent.'},
  {title: 'Souverain, hébergé en Europe.', body: 'Infrastructure conforme au RGPD. Vos données ne partent pas ailleurs.'},
  {title: 'Des skills réutilisables.', body: 'Vos traitements récurrents deviennent des capacités rejouables, maintenues par Living Data ou construites sur mesure.'},
  {title: 'Multi-modèles.', body: 'Votre travail ne dépend pas d’un seul fournisseur d’IA : le modèle est choisi selon vos exigences, pas imposé.'}
]

function WhyUs() {
  return (
    <section className='ld-section ld-section--pad ld-problem'>
      <div className='ld-wrap'>
        <Reveal>
          <Eyebrow>Pourquoi Living Data</Eyebrow>
          <h2 className='ld-h2' style={{maxWidth: '24ch'}}>
            Derrière l’outil, dix ans d’expertise de la donnée.
          </h2>
        </Reveal>

        <div className='ld-why-grid'>
          {WHY.map((item, i) => (
            <Reveal key={item.title} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <article className='ld-why'>
                <span className='ld-why__no'>{String(i + 1).padStart(2, '0')}</span>
                <h3 className='ld-h3'>{item.title}</h3>
                <p className='ld-body' style={{fontSize: '0.93rem'}}>{item.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

const SOON = [
  {title: 'Publication d’artefacts', desc: 'en interne ou en public'},
  {title: 'Équipes & organisations', desc: 'le travail partagé'},
  {title: 'Choix du modèle', desc: 'le LLM, dans l’app'},
  {title: 'Agents personnalisés', desc: 'vos traitements, réutilisables'}
]

function Roadmap() {
  return (
    <section className='ld-section ld-section--pad'>
      <div className='ld-wrap'>
        <Reveal>
          <Eyebrow>Ce qui arrive</Eyebrow>
          <h2 className='ld-h2'>La suite est déjà en chantier.</h2>
        </Reveal>
        <Reveal delay={1}>
          <ul className='ld-roadmap'>
            {SOON.map(item => (
              <li key={item.title} className='ld-roadmap__item'>
                <span className='ld-roadmap__title'>{item.title}</span>
                <span className='ld-roadmap__desc'>{item.desc}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  )
}

const TRUST = [
  {slug: 'gouv', alt: 'Gouvernement'},
  {slug: 'anct', alt: 'ANCT'},
  {slug: 'beta', alt: 'beta.gouv'},
  {slug: 'etalab', alt: 'Etalab'},
  {slug: 'grand-est', alt: 'Région Grand Est'},
  {slug: 'ifremer', alt: 'Ifremer'},
  {slug: 'ign', alt: 'IGN'},
  {slug: 'transition-eco', alt: 'Ministère de la Transition écologique'},
  {slug: 'banque-mondiale', alt: 'Banque mondiale'},
  {slug: 'worldline', alt: 'Worldline'}
]

function TrustLogos() {
  return (
    <section className='ld-section ld-trust'>
      <div className='ld-trust__inner'>
        <Reveal>
          <p className='ld-trust__label'>Ils ont fait confiance à Living Data</p>
          <div className='ld-trust__logos'>
            {TRUST.map(client => (
              <img
                key={client.slug}
                className='ld-trust__logo'
                src={`/clients/${client.slug}.png`}
                alt={client.alt}
                loading='lazy'
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className='ld-section ld-final ld-project'>
      <ContourField />
      <div className='ld-final__inner'>
        <Reveal>
          <Eyebrow>Commencez maintenant</Eyebrow>
          <h2 className='ld-h2'>Lancez-vous, même sans vos données.</h2>
          <p className='ld-project__lead' style={{marginInline: 'auto'}}>
            L’Early Access est gratuit au lancement. Les skills et référentiels déjà en place donnent de quoi produire tout de suite, et la documentation est pleine d’exemples pour s’inspirer.
          </p>
          <div className='ld-final__cta'>
            <Link to={SIGN_UP} className='ld-btn ld-btn--primary' style={{background: '#fff', color: '#1f2e3d'}}>
              Créer un compte <ArrowRight />
            </Link>
            <Link to={DOCS} className='ld-textlink' style={{color: '#c9ead9'}}>
              Lire la documentation
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

export function LandingPage() {
  const root = useReveal()

  return (
    <div ref={root} className='ld-home'>
      <Hero />
      <Problem />
      <Agent />
      <Results />
      <WhyUs />
      <TrustLogos />
      <Roadmap />
      <FinalCta />
    </div>
  )
}
